import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toneForValue } from './ui/Badge';

interface TechnicianLocation {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  role: string;
  departmentId: string;
  departmentName: string;
  phone: string;
  latitude: number;
  longitude: number;
  lastUpdated: string;
  status: 'online' | 'offline' | 'busy' | 'available';
  currentAssignment?: {
    id: string;
    reportTitle: string;
    status: string;
    priority: string;
  };
  isActive: boolean;
}

export interface ReportPoint {
  id: string;
  reportNumber?: string;
  title: string;
  status: string;
  priority?: string;
  latitude: number;
  longitude: number;
  address?: string;
  categoryName?: string;
  createdAt?: string;
}

interface OpenStreetMapProps {
  technicians: TechnicianLocation[];
  onTechnicianSelect: (technician: TechnicianLocation) => void;
  selectedTechnicianId?: string;
  reports?: ReportPoint[];
  onReportSelect?: (report: ReportPoint) => void;
}

// Mirrors Badge.tsx's tone semantics (the single source of truth for
// status -> meaning across the portal) as hex colors, since Leaflet markers
// need real CSS colors rather than Tailwind classes.
const TONE_HEX: Record<string, string> = {
  neutral: '#6b7280',
  info: '#2563eb',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
};

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const OpenStreetMap: React.FC<OpenStreetMapProps> = ({
  technicians,
  onTechnicianSelect,
  selectedTechnicianId,
  reports = [],
  onReportSelect,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const popupsRef = useRef<Map<string, L.Popup>>(new Map());
  const reportMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default center (Howick, KZN - Umngeni Municipality)
  const MAP_DEFAULT_CENTER: [number, number] = [-29.4858, 30.2306];
  const MAP_DEFAULT_ZOOM = 14;

  // Custom marker icons
  const createCustomIcon = (status: string): L.DivIcon => {
    const colors = {
      available: '#10b981',
      busy: '#f59e0b',
      online: '#3b82f6',
      offline: '#6b7280'
    };

    const color = colors[status as keyof typeof colors] || '#6b7280';
    
    return L.divIcon({
      html: `
        <div style="
          width: 32px; 
          height: 32px; 
          background: ${color}; 
          border: 3px solid white; 
          border-radius: 50%; 
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        ">
          <span style="
            width: 12px; 
            height: 12px; 
            background: white; 
            border-radius: 50%;
          "></span>
        </div>
      `,
      className: 'custom-marker',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });
  };

  // Diamond shape (vs. the technicians' circle) so the two layers stay
  // visually distinct at a glance even when they overlap on the map.
  const createReportIcon = (status: string): L.DivIcon => {
    const color = TONE_HEX[toneForValue(status)] ?? '#6b7280';

    return L.divIcon({
      html: `
        <div style="
          width: 20px;
          height: 20px;
          background: ${color};
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          transform: rotate(45deg);
        "></div>
      `,
      className: 'custom-report-marker',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      popupAnchor: [0, -10]
    });
  };

  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up existing map if it exists
    if (mapInstanceRef.current) {
      console.log('Cleaning up existing map...');
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    try {
      console.log('Initializing OpenStreetMap...');
      
      // Create the map instance
      const map = L.map(mapRef.current, {
        center: MAP_DEFAULT_CENTER,
        zoom: MAP_DEFAULT_ZOOM,
        zoomControl: true,
        attributionControl: true
      });

      console.log('Map instance created, adding tile layers...');

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);

      // Add additional tile layers for variety
      const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '© <a href="https://www.esri.com/">Esri</a>',
        maxZoom: 19
      });

      const streetLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      });

      // Layer control
      const baseMaps = {
        "Street": streetLayer,
        "Satellite": satelliteLayer
      };

      L.control.layers(baseMaps).addTo(map);

      console.log('Tile layers added successfully');

      // Store map instance
      mapInstanceRef.current = map;
      setIsLoading(false);

      // Add click handler to close popups when clicking on map
      map.on('click', () => {
        map.closePopup();
      });

      console.log('OpenStreetMap initialized successfully');
    } catch (err) {
      console.error('Failed to initialize OpenStreetMap:', err);
      setError(`Failed to load map: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        console.log('Cleaning up map on unmount...');
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current && technicians.length > 0) {
      updateMarkers();
    }
  }, [technicians]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      updateReportMarkers();
    }
  }, [reports]);

  useEffect(() => {
    if (selectedTechnicianId && mapInstanceRef.current) {
      const marker = markersRef.current.get(selectedTechnicianId);
      if (marker) {
        mapInstanceRef.current.setView(marker.getLatLng(), 16);
        
        // Show popup for selected technician
        const popup = popupsRef.current.get(selectedTechnicianId);
        if (popup) {
          marker.openPopup();
        }
      }
    }
  }, [selectedTechnicianId]);



  const updateMarkers = () => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      mapInstanceRef.current!.removeLayer(marker);
    });
    markersRef.current.clear();
    popupsRef.current.clear();

    // Add new markers
    technicians.forEach(technician => {
      if (technician.latitude && technician.longitude) {
        const marker = L.marker([technician.latitude, technician.longitude], {
          icon: createCustomIcon(technician.status)
        });

        // Create popup content
        const popupContent = `
          <div style="min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 16px; font-weight: 600;">
              ${technician.firstName} ${technician.lastName}
            </h3>
            <div style="margin-bottom: 8px;">
              <span style="
                display: inline-block;
                padding: 4px 8px;
                background: ${technician.status === 'available' ? '#d1fae5' : 
                           technician.status === 'busy' ? '#fef3c7' : 
                           technician.status === 'online' ? '#dbeafe' : '#f3f4f6'};
                color: ${technician.status === 'available' ? '#065f46' : 
                        technician.status === 'busy' ? '#92400e' : 
                        technician.status === 'online' ? '#1e40af' : '#374151'};
                border-radius: 6px;
                font-size: 12px;
                font-weight: 500;
                text-transform: capitalize;
              ">
                ${technician.status}
              </span>
            </div>
            <div style="margin-bottom: 4px; font-size: 14px; color: #6b7280;">
              <strong>Role:</strong> ${technician.role.replace('_', ' ')}
            </div>
            <div style="margin-bottom: 4px; font-size: 14px; color: #6b7280;">
              <strong>Department:</strong> ${technician.departmentName}
            </div>
            <div style="margin-bottom: 4px; font-size: 14px; color: #6b7280;">
              <strong>Phone:</strong> ${technician.phone}
            </div>
            ${technician.currentAssignment ? `
              <div style="margin-top: 8px; padding: 8px; background: #f9fafb; border-radius: 6px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">Current Assignment:</div>
                <div style="font-size: 14px; color: #374151; font-weight: 500;">
                  ${technician.currentAssignment.reportTitle}
                </div>
                <div style="font-size: 12px; color: #6b7280;">
                  Priority: ${technician.currentAssignment.priority}
                </div>
              </div>
            ` : ''}
            <div style="margin-top: 8px; font-size: 12px; color: #9ca3af;">
              Last updated: ${new Date(technician.lastUpdated).toLocaleString()}
            </div>
          </div>
        `;

        const popup = L.popup({
          maxWidth: 300,
          className: 'technician-popup'
        }).setContent(popupContent);

        // Add click handler
        marker.on('click', () => {
          onTechnicianSelect(technician);
        });

        // Add marker to map
        if (mapInstanceRef.current) {
          marker.addTo(mapInstanceRef.current);
          marker.bindPopup(popup);
        }

        // Store references
        markersRef.current.set(technician.id, marker);
        popupsRef.current.set(technician.id, popup);
      }
    });
  };

  const updateReportMarkers = () => {
    if (!mapInstanceRef.current) return;

    // Clear existing report markers
    reportMarkersRef.current.forEach(marker => {
      mapInstanceRef.current!.removeLayer(marker);
    });
    reportMarkersRef.current.clear();

    reports.forEach(report => {
      if (report.latitude && report.longitude) {
        const marker = L.marker([report.latitude, report.longitude], {
          icon: createReportIcon(report.status)
        });

        const popupContent = `
          <div style="min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 16px; font-weight: 600;">
              ${report.title}
            </h3>
            <div style="margin-bottom: 8px;">
              <span style="
                display: inline-block;
                padding: 4px 8px;
                background: #f3f4f6;
                color: #374151;
                border-radius: 6px;
                font-size: 12px;
                font-weight: 500;
                text-transform: capitalize;
              ">
                ${report.status.replace(/_/g, ' ')}
              </span>
              ${report.priority ? `
                <span style="
                  display: inline-block;
                  margin-left: 4px;
                  padding: 4px 8px;
                  background: #fef2f2;
                  color: #991b1b;
                  border-radius: 6px;
                  font-size: 12px;
                  font-weight: 500;
                  text-transform: capitalize;
                ">
                  ${report.priority}
                </span>
              ` : ''}
            </div>
            ${report.categoryName ? `
              <div style="margin-bottom: 4px; font-size: 14px; color: #6b7280;">
                <strong>Category:</strong> ${report.categoryName}
              </div>
            ` : ''}
            ${report.address ? `
              <div style="margin-bottom: 4px; font-size: 14px; color: #6b7280;">
                <strong>Address:</strong> ${report.address}
              </div>
            ` : ''}
            ${report.reportNumber ? `
              <div style="margin-top: 8px; font-size: 12px; color: #9ca3af;">
                Report #${report.reportNumber}
              </div>
            ` : ''}
          </div>
        `;

        const popup = L.popup({
          maxWidth: 300,
          className: 'report-popup'
        }).setContent(popupContent);

        marker.on('click', () => {
          if (onReportSelect) onReportSelect(report);
        });

        if (mapInstanceRef.current) {
          marker.addTo(mapInstanceRef.current);
          marker.bindPopup(popup);
        }

        reportMarkersRef.current.set(report.id, marker);
      }
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Map Error</div>
          <div className="text-red-500 text-sm">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <div className="text-gray-600 dark:text-gray-400">Loading map...</div>
          </div>
        </div>
      )}
      <div 
        ref={mapRef} 
        className="w-full h-96 rounded-lg border border-gray-200"
        style={{ minHeight: '400px' }}
      />
    </div>
  );
};

export default OpenStreetMap;
